// composition.js — canonical Risk-Adjusted composition + adjusted score.
//
// Single source of truth for the five-bucket disposition composition
// (Patriot Verified / Risk Accepted / Customer Managed Solution /
// Customer Remediated / Open Gap) and the risk-adjusted score. The dashboard
// donut (SecureShield365.js categoryData) and the PDF export (ExecutiveReport.js)
// both consume this so the two views can never drift.
//
// Math is lifted VERBATIM from the original categoryData useMemo (Phase E2) —
// same counted-settings gate, same credit/adjEarned logic, same canonical-base
// adjusted-score derivation, same composition bucketing. The dashboard donut
// output must remain byte-identical after the refactor.
//
// Framework-agnostic, plain JS — no @react-pdf/renderer or xlsx imports — so both
// the dashboard and the PDF export can consume it without pulling in each other's
// dependencies.

// Override keys that grant score credit (treated as fully earned in the adjusted
// view). Single source of truth — re-exported / re-used wherever credit is gated.
export const CREDIT_OVERRIDES = ["risk_accepted", "customer_managed_solution", "customer_remediated"];

// Phase E2 — Semantic palette for the Risk-Adjusted composition, keyed by the
// override state / composition bucket so colors are consistent regardless of
// slice order. Shared by the dashboard donut and the PDF composition bar.
export const OVERRIDE_SLICE_COLORS = {
  verified:            "#22C55E",  // green — actually passing per Patriot scoring
  risk_accepted:       "#FBBF24",  // amber — customer accepted ongoing risk
  customer_managed_solution:  "#14B8A6",  // teal — 3rd-party solution covers this control
  customer_remediated: "#A3E635",  // light green — claimed fixed, pending verification
  unresolved_gap:      "#F87171",  // soft red — real remaining gap
};

// Live disposition labels — the EXACT strings the dashboard uses, so the PDF
// legend and pills read identically. "unresolved_gap" shows as "Open Gap".
export const COMPOSITION_LABELS = {
  verified:            "Patriot Verified",
  risk_accepted:       "Risk Accepted",
  customer_managed_solution: "Customer Managed Solution",
  customer_remediated: "Customer Remediated",
  unresolved_gap:      "Open Gap",
};

// isCountedInScore(r) — the frontend's mirror of backend is_counted_in_score
// (backend/scoring/scoring.py:37-67). ONE STATEMENT; both loops below call it,
// and neither restates it.
//
// THE RULE OF RECORD (PR #396, product decision 2026-07-28, James Metcalfe): a
// setting is counted iff scoring_mode == "scored" AND result != "NotApplicable"
// AND result is not a Not Assessed verdict (Skipped / Error / NotRun). Not
// Assessed rows leave the denominator entirely — they earn nothing and count
// nothing; coverage visibility (build_coverage_summary) replaced the older
// keep-in-earning-0 fail-safe.
//
// scored_status is a CLOSED 4-way set where it exists (scoring.py:556-569):
// advisory / not_applicable / evaluation_failed / scored, rubric mode winning
// over result. It is tested here as three EXCLUSIONS plus one inclusion —
// deliberately NOT an allowlist on "scored" — because the field is ABSENT on
// payloads written before it existed, and an allowlist would drop every row of
// an archived or legacy-import report: pct 0, adj 0, empty donut, empty PDF
// composition, and no error anywhere. The absent case falls back to `result`
// the same way verdictOf() already does (overrideStatus.js:130) and the same
// way scoring.py derives scored_status in the first place.
//
// ONE KNOWN GAP, STATED: the backend rule's first condition is
// scoring_mode == "scored", and the rec row carries no scoring_mode — only the
// derived scored_status. So on a legacy payload an advisory row IS counted.
// That is exactly the pre-existing behaviour (the old gate also learned
// "advisory" only from scored_status), so the fallback introduces no new drift.
// Widening it needs a new field on the rec row and is not this change's job.
export function isCountedInScore(r) {
  const ss = r?.scored_status;
  if (ss === "advisory" || ss === "not_applicable" || ss === "evaluation_failed") return false;
  if (ss === "scored") return true;
  // ── Absent scored_status (legacy payload). Mirror the RESULT half of the
  // rule. The final `return true` is deliberate and matches the backend: an
  // unrecognised engine verdict IS counted, earning whatever it earned —
  // _evaluate_test passes maester_result through verbatim in several
  // fall-through branches (scoring.py:333/352/361), so `result` is NOT a
  // closed set. Returning false here — the shape verdictOf() has, because it
  // answers a different question — would DROP a counted row.
  const result = r?.result;
  if (result === "NotApplicable") return false;
  if (result === "Skipped" || result === "Error" || result === "NotRun") return false;
  return true;
}

// isNotAssessedFailure(r) — true when the row is out of the score BECAUSE it was
// never assessed (Skipped / Error / NotRun), as opposed to advisory or N/A.
// Same two-branch shape as isCountedInScore, for the same legacy-payload reason.
function isNotAssessedFailure(r) {
  const ss = r?.scored_status;
  if (ss === "evaluation_failed") return true;
  if (ss) return false;   // any other known scored_status: not this population
  const result = r?.result;
  return result === "Skipped" || result === "Error" || result === "NotRun";
}

// notAssessedOverriddenLine(nao) — the ONE sentence both surfaces render, so the
// dashboard and the PDF cannot drift in wording. Returns null when there is
// nothing to say (rows === 0), which is how each surface decides to render
// nothing at all.
//
// APPROVED CUSTOMER-FACING COPY — James Metcalfe, 2026-08-20
// (docs/plans/risk-adjusted-gate.md §16 Q1). The approval is on the WORDING:
//   "Not assessed — override on file: 59 settings (119 pts), no score credit"
// Only the two counts interpolate, per customer. Do not reword the fixed parts,
// and do not hardcode any customer's numbers.
export function notAssessedOverriddenLine(nao) {
  const rows = nao?.rows || 0;
  if (rows <= 0) return null;
  const prefix = "Not assessed — override on file: ";
  const counts = `${rows} setting${rows === 1 ? "" : "s"} (${nao.pts || 0} pts)`;
  const suffix = ", no score credit";
  return { prefix, counts, suffix, text: `${prefix}${counts}${suffix}` };
}

// computeComposition(recs, settingDetails, totals)
//
//   recs           — normalized setting rows ({ id, cat, category, scored_status,
//                     max_possible, earned, ... }) as built by SecureShield365.js.
//   settingDetails — map keyed by setting id → { override, ... } (from report.overrides).
//   totals         — report.totals ({ score_pct, max_possible_total }) — supplies the
//                     canonical adjusted-score base + denominator. May be undefined.
//
// Returns the SAME object shape categoryData returned before the extraction,
// plus notAssessedOverridden:
//   { pct, adj, patriotSlices, adjustedSlices, riskAdjustedComposition,
//     byCategory, bySecurityDomain, notAssessedOverridden }
export function computeComposition(recs, settingDetails, totals) {
  if (!recs || recs.length === 0) {
    return { pct: 0, adj: 0, patriotSlices: [], adjustedSlices: [], notAssessedOverridden: { rows: 0, pts: 0 } };
  }
  const byCategory = {};         // keyed by r.cat (Product / workload)
  const bySecurityDomain = {};   // keyed by r.category (Security Domain), "Unclassified" when absent
  let totalMax = 0;
  let totalEarnedPatriot = 0;
  let creditPoints = 0;  // extra points overrides ADD on top of earned (gated)
  // F1 — the population this gate makes VANISH: rows excluded because nothing
  // was ever assessed, that nonetheless carry a credit override. Tallied here,
  // in the same pass that drops them, so the count can never disagree with the
  // population it names. It is deliberately NOT part of riskAdjustedComposition:
  // there is no code path by which it could enter total, the bar, the donut arc,
  // creditPoints, delta, or adj.
  let notAssessedOverriddenRows = 0;
  let notAssessedOverriddenPts = 0;

  for (const r of recs) {
    // Counted-settings gate — mirrors backend is_counted_in_score
    // (backend/scoring/scoring.py:37-67) via the one shared helper above.
    // The mirror was broken from 2026-07-28 (PR #396) until this change: it
    // excluded only advisory and not_applicable and KEPT evaluation_failed, so
    // this numerator ran over a larger population than the server's denominator
    // and Math.min(100, …) hid the overflow. Not Assessed rows now leave, the
    // same way they leave report.totals.max_possible_total.
    if (!isCountedInScore(r)) {
      // Not-assessed-with-credit-override: count it before dropping it (F1).
      // advisory / not_applicable rows are deliberately NOT in this population —
      // the old gate already excluded them, so a credit override on one already
      // contributed nothing and nothing about them changes here.
      if (isNotAssessedFailure(r) && CREDIT_OVERRIDES.includes(settingDetails?.[r.id]?.override)) {
        notAssessedOverriddenRows += 1;
        notAssessedOverriddenPts += r.max_possible || 0;
      }
      continue;
    }
    const cat = r.cat || "Other";
    const max = r.max_possible || 0;
    const earned = r.earned || 0;
    const sd = settingDetails?.[r.id];
    const override = sd?.override;
    const isCredit = override === "risk_accepted" || override === "customer_managed_solution" || override === "customer_remediated";
    const adjEarned = isCredit ? max : earned;
    // Points an override ADDS over what was already earned — can't double-count
    // a setting already passing (max - earned == 0 there).
    if (isCredit) creditPoints += max - earned;

    if (!byCategory[cat]) byCategory[cat] = { max: 0, earnedPatriot: 0, earnedAdjusted: 0 };
    byCategory[cat].max += max;
    byCategory[cat].earnedPatriot += earned;
    byCategory[cat].earnedAdjusted += adjEarned;

    // Parallel accumulation by Security Domain (r.category). Same gate, same
    // max/earned/adjEarned values as the Product bucket above — just a second
    // grouping key. Settings with no category land in "Unclassified".
    const dom = r.category || "Unclassified";
    if (!bySecurityDomain[dom]) bySecurityDomain[dom] = { max: 0, earnedPatriot: 0, earnedAdjusted: 0 };
    bySecurityDomain[dom].max += max;
    bySecurityDomain[dom].earnedPatriot += earned;
    bySecurityDomain[dom].earnedAdjusted += adjEarned;

    totalMax += max;
    totalEarnedPatriot += earned;
  }

  const pct = totalMax > 0 ? Math.round(1000 * totalEarnedPatriot / totalMax) / 10 : 0;
  // adj shares d.score's exact canonical base (report.totals.score_pct), then
  // adds the override delta — so at zero overrides adj === base === d.score
  // with no recompute double-rounding drift. denom prefers the canonical
  // max_possible_total served on the wire; falls back to the gated totalMax.
  const base = totals ? Math.round(totals.score_pct * 10) / 10 : pct;
  const denom = (totals && typeof totals.max_possible_total === "number" && totals.max_possible_total > 0)
    ? totals.max_possible_total
    : totalMax;
  const delta = denom > 0 ? (creditPoints / denom) * 100 : 0;
  const adj = Math.min(100, Math.round((base + delta) * 10) / 10);

  const buildSlices = (earnedKey) => {
    const rows = Object.entries(byCategory)
      .map(([cat, d]) => ({ cat, gap: d.max - d[earnedKey], max: d.max }))
      .filter(r => r.gap > 0)
      .sort((a, b) => b.gap - a.gap);
    if (rows.length <= 8) return rows;
    const top = rows.slice(0, 8);
    const other = rows.slice(8).reduce((a, r) => ({ cat: "Other", gap: a.gap + r.gap, max: a.max + r.max }), { cat: "Other", gap: 0, max: 0 });
    return [...top, other];
  };

  // Phase E2 — Composition view for the Risk-Adjusted donut.
  // Instead of category breakdown, show what makes up the score:
  //   - Patriot Verified: settings actually Passing (result === "Passed", earned == max)
  //   - Risk Accepted, Customer Managed Solution, Customer Remediated: settings credited via override
  //   - Unresolved Gap: still failing, no override (or Planned which doesn't credit)
  // Sized by points (max_possible) so severity matters: a Critical Risk Accepted
  // contributes more to the slice than a Low Risk Accepted.
  const composition = {
    verified: 0,
    risk_accepted: 0,
    customer_managed_solution: 0,
    customer_remediated: 0,
    unresolved_gap: 0,
  };
  for (const r of recs) {
    // Same counted-settings gate as the totals loop (one classifier, called
    // twice — never restated), so composition slice sizing matches the gated
    // denominator.
    if (!isCountedInScore(r)) continue;
    const max = r.max_possible || 0;
    const earned = r.earned || 0;
    const sd = settingDetails?.[r.id];
    const override = sd?.override;

    if (earned === max && max > 0) {
      // Setting is actually passing per scoring engine
      composition.verified += max;
    } else if (override === "risk_accepted") {
      composition.risk_accepted += max;
    } else if (override === "customer_managed_solution") {
      composition.customer_managed_solution += max;
    } else if (override === "customer_remediated") {
      composition.customer_remediated += max;
    } else {
      // No override or "planned" — still counts as unresolved gap.
      // Planned signals intent to fix but doesn't yet credit score.
      composition.unresolved_gap += max - earned;
      // Partial credit on the verified bucket for partial passes
      if (earned > 0 && earned < max) {
        composition.verified += earned;
      }
    }
  }

  // Build composition slices. Always show Verified + Unresolved Gap; override
  // types only render when > 0 (cleaner for customers with no overrides yet).
  const compositionSlices = [
    { key: "verified",            label: COMPOSITION_LABELS.verified,            pts: composition.verified,            always: true },
    // Phase E2 polish — always show the three credit-granting overrides in the
    // legend so customers see what they could affect by applying an override,
    // even when their pts is 0. Slices with 0 pts render as 0% in the legend
    // but the entry remains visible to communicate the option exists.
    { key: "risk_accepted",       label: COMPOSITION_LABELS.risk_accepted,       pts: composition.risk_accepted,       always: true },
    { key: "customer_managed_solution",  label: COMPOSITION_LABELS.customer_managed_solution, pts: composition.customer_managed_solution,  always: true },
    { key: "customer_remediated", label: COMPOSITION_LABELS.customer_remediated, pts: composition.customer_remediated, always: true },
    { key: "unresolved_gap",      label: COMPOSITION_LABELS.unresolved_gap,      pts: composition.unresolved_gap,      always: true },
  ].filter(s => s.always || s.pts > 0);

  return {
    pct,
    adj,
    patriotSlices: buildSlices("earnedPatriot"),
    adjustedSlices: buildSlices("earnedAdjusted"),
    riskAdjustedComposition: compositionSlices,
    byCategory,         // per-Product { max, earnedPatriot, earnedAdjusted }
    bySecurityDomain,   // per-Security-Domain { max, earnedPatriot, earnedAdjusted }
    // F1 — visible, and structurally non-crediting. Rendered as a muted count
    // line beneath the composition legend on the dashboard and in the PDF.
    notAssessedOverridden: { rows: notAssessedOverriddenRows, pts: notAssessedOverriddenPts },
  };
}
